import {BaseElementAsync, BASIS, OSkinableInit} from "/@back@/commons/basis.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {IShortDescCtx, ITEM} from "/@lib@/wsp/item.js";
import {IItemUiEnv} from "/@back@/wsp/views/itemMain.js";
import {JWspUriChangeMsg, WSP, wspCd, wspRef} from "/@lib@/wsp/wsp.js";
import {EWspChangesEvts, EWspWorkingSt, WspsLivePlace} from "/@lib@/wsp/wspsLive.js";
import {SRC, srcRef} from "/@lib@/wsp/src.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {BarActions, OBarActionsInit} from "/@back@/commons/widgets/bars.js";
import {Action, IAction} from "/@lib@/commons/actions.js";
import {OPopupAnchor} from "/@back@/commons/widgets/popupable.js";

export interface OImgSizeConfig extends OSkinableInit {
	reg?: IReg<IItemUiEnv>,
	dimsTransform: string,
	stdCharSize?: {
		defaultValue?: number | ((width: number, dpi?: number) => number),
		textRef?: string
	},
	limitCharSize?: {
		defaultValue?: number,
		textRef?: string
	},
	rotateForbidden?: {
		defaultValue?: boolean
	},
	/** pour drop. */
	writePerms: string | string[],
	/** toolbar */
	actions?: IAction<IShortDescCtx>[],
	actionContext?: IShortDescCtx;
}

const STDCHARSIZE_TXTREF = "Ceci est un texte de référence qui vous permet d'adapter la taille de l'image en fonction de la taille des caractères et d'une largeur standard de paragraphe.";
const LIMITCHARSIZE_TXTREF = "Ceci est un texte de référence qui vous permet d'adapter la taille de l'image. Réglez le curseur d'échelle ci-contre de telle sorte que l'image demeure intelligible.";
const TXTREF_HELP = "Si votre image comporte des caractères (schéma, tableau, copie d'écran...), réglez le curseur d'échelle ci-contre pour que les caractères de l'image de taille « normale » correspondent à la taille des caractères de ce texte. Si votre image ne contient pas de caractères (photo, illustration), réglez le curseur d'échelle pour donner l'importance qui convient à l'image en rapport avec ce paragraphe et particulièrement avec sa largeur.";

type IImgSizeState = {
	stdCharSize: number,
	limitCharSize: number,
	rotateForbid: boolean,
};

export class ImgSize extends BaseElementAsync {

	static addResView(sizeConfig: OImgSizeConfig | null, actionsList: string, parent: Node, resView: HTMLElement, reg: IReg<IItemUiEnv>, actionCtx?: IShortDescCtx) {
		if (!actionCtx) actionCtx = reg.env.srcView
		const actions = reg.mergeLists<IAction<IShortDescCtx>>(actionsList, "actions:wsp:binaryStream").filter((a) => a.isInstantiable(actionCtx));
		if (sizeConfig) {
			const imgSizeInit = BASIS.newInit(sizeConfig, reg);
			if (actions.length > 0) {
				imgSizeInit.actions = actions;
				imgSizeInit.actionContext = actionCtx;
			}
			parent.appendChild(<ImgSize î={imgSizeInit}>{resView}</ImgSize>);
		} else {
			if (actions.length > 0) parent.appendChild(
				<BarActions î={{actions, actionContext: actionCtx, reg: reg} as OBarActionsInit<IShortDescCtx>}/>
			);
			parent.appendChild(resView);
		}
	}

	reg: IReg<IItemUiEnv>;

	protected _locked: boolean = false;
	protected _propsWspRef: string;
	protected _dimsTransform: string;
	protected _dims: [number, number];
	protected _dpi: number;
	protected _propsElt: Element;
	protected _savePropsReq: number = 0;
	protected _updateStdCharReq: number = 0;
	protected _updateLimitCharReq: number = 0;
	protected _history: IImgSizeState[] = [];
	protected _currentStateIdx = -1;
	protected _isDirty = false;

	protected _onWspUriChange: (msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') => void;
	protected _onNewWspWorkingState: (wspRef: wspRef, wspCd: wspCd, srcRef: srcRef, st: EWspWorkingSt) => void;

	protected _stdCharSize: {
		area: HTMLDivElement,
		rangeInput: HTMLInputElement,
		percentInput: HTMLInputElement,
		valueInput: HTMLInputElement,
		/** Valeur par défaut pour initialisation après récupération des dimensions */
		defaultValue: number | ((width: number, dpi?: number) => number)
		/** Valeur en pourcentage (cf eu.scenari.transform.img.IImgSrc.getStandardCharSize()) */
		value: number
	};
	protected _limitCharSize: {
		area: HTMLDivElement,
		rangeInput: HTMLInputElement,
		percentInput: HTMLInputElement,
		valueInput: HTMLInputElement,
		defaultValue: number,
		/** Valeur en pourcentage (cf eu.scenari.transform.img.IImgSrc.getLimitCharSize()) */
		value: number
	};
	protected _rotateForbid: {
		area: HTMLDivElement,
		input: HTMLInputElement,
		defaultValue: boolean,
		value: boolean
	};

	protected _writePerms: string | string[];

	protected _toolbar: BarActions<ImgSize>;
	protected _sizeBox: HTMLSlotElement;
	protected _sizeRefElem: HTMLDivElement;
	protected _rulerElem: HTMLDivElement;
	protected _txtRefElem: HTMLDivElement;
	protected _notifAnchor: OPopupAnchor = {posFrom: this, fromY: "bottom", targetY: "bottom", fromX: "middle", targetX: "middle"};

	protected async _initialize(init: OImgSizeConfig) {
		this.reg = this.findReg<IItemUiEnv>(init);

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("scroll/small", sr);
		this._initAndInstallSkin(this.localName, init);
		if (init.dimsTransform) this._dimsTransform = init.dimsTransform;
		else console.warn("No dimsTransform specified");


		this._txtRefElem = <div id="txtRef"/> as HTMLDivElement;
		this._sizeBox = <div id="sizeBox">
			<slot/>
		</div> as HTMLSlotElement;

		this._writePerms = init.writePerms || "action.itemMainView#edit.content";

		// orient=vertical sur les input de type ranges pour Firefox
		if (init.stdCharSize) {
			this._stdCharSize = {
				area: null,
				rangeInput: <input type="range" min="1" max="300" step="any" orient="vertical"/> as HTMLInputElement,
				percentInput: <input type="number" min="1" max="300" step="any"/> as HTMLInputElement,
				valueInput: <input type="number"/> as HTMLInputElement,
				defaultValue: init.stdCharSize.defaultValue || 10,
				value: NaN
			};
			this._stdCharSize.rangeInput.oninput = this._stdCharSize.percentInput.oninput = (ev: Event) => {
				this._stdCharSize.value = parseFloat((ev.target as HTMLInputElement).value);
				this._onStdCharSizeUpdate(ev.currentTarget as HTMLElement);
				this._setDirty(true);
				this._requestSaveProps();
			};
			this._stdCharSize.valueInput.oninput = (ev: Event) => {
				this._stdCharSize.value = parseFloat((ev.target as HTMLInputElement).value) * 600 / this._dims[0];
				this._onStdCharSizeUpdate(ev.currentTarget as HTMLElement);
				this._setDirty(true);
				this._requestSaveProps();
			};
			this._stdCharSize.rangeInput.onchange = this._stdCharSize.percentInput.onchange = this._stdCharSize.valueInput.onchange = () => {
				this._pushStateToHistory();
			};

			this._stdCharSize.area = <div class="sizeArea" id="stdCharSizeArea">
				<div class="areaTitle">Taille de l'image</div>
				<label title="Taille de l'image en pourcentage de sa taille d'origine">
					{this._stdCharSize.percentInput}
					<span>%</span>
				</label>
				{this._stdCharSize.rangeInput}
				<label title="Taille limite de l'image en nombre de caractères">
					{this._stdCharSize.valueInput}
					<span>car.</span>
				</label>
			</div> as HTMLDivElement;
		}

		if (init.limitCharSize) {
			const defaultVal = 'defaultValue' in init.limitCharSize ? 1000 / init.limitCharSize.defaultValue : 80;
			this._limitCharSize = {
				area: null,
				rangeInput: <input type="range" min="1" max="100" step="any" orient="vertical"/> as HTMLInputElement,
				percentInput: <input type="number" min="1" max="100" step="any"/> as HTMLInputElement,
				valueInput: <input type="number"/> as HTMLInputElement,
				defaultValue: defaultVal,
				value: defaultVal
			};
			this._limitCharSize.rangeInput.oninput = this._limitCharSize.percentInput.oninput = (ev: Event) => {
				this._limitCharSize.value = parseFloat((ev.target as HTMLInputElement).value);
				this._onLimitCharSizeUpdate(this);
				this._setDirty(true);
				this._requestSaveProps();
			};
			this._limitCharSize.valueInput.oninput = (ev: Event) => {
				const stdCharWidth = this._stdCharSize ? this._dims[0] * this._stdCharSize.value / 100 : this._dims[0];
				this._limitCharSize.value = parseFloat((ev.target as HTMLInputElement).value) * 600 / stdCharWidth;
				this._onLimitCharSizeUpdate(this);
				this._setDirty(true);
				this._requestSaveProps();
			};

			this._limitCharSize.rangeInput.onchange = this._limitCharSize.percentInput.onchange = this._limitCharSize.valueInput.onchange = () => {
				this._pushStateToHistory();
			};

			this._limitCharSize.area = <div class="sizeArea" id="limitCharSizeArea">
				<div class="areaTitle">Limite de lisibilité</div>
				<label title="Taille limite de l'image en pourcentage de sa taille d'origine">
					{this._limitCharSize.percentInput}
					<span>%</span>
				</label>
				{this._limitCharSize.rangeInput}
				<label title="Taille limite de l'image en nombre de caractères">
					{this._limitCharSize.valueInput}
					<span>car.</span>
				</label>
			</div> as HTMLDivElement;

			if (init.stdCharSize) {
				this._limitCharSize.area.addEventListener('focusin', () => {
					this._txtRefElem.textContent = init.limitCharSize.textRef || LIMITCHARSIZE_TXTREF;
					this._onLimitCharSizeUpdate();
				});
				this._limitCharSize.area.addEventListener('focusout', () => {
					this._txtRefElem.textContent = init.stdCharSize.textRef || STDCHARSIZE_TXTREF;
					this._onStdCharSizeUpdate();
				});
			}
		}

		if (init.rotateForbidden) {
			const defaultVal = 'defaultValue' in init.rotateForbidden ? init.rotateForbidden.defaultValue : false;
			this._rotateForbid = {
				area: null,
				input: <input type="checkbox" checked={!defaultVal ? "" : undefined}/> as HTMLInputElement,
				defaultValue: defaultVal,
				value: defaultVal
			};
			this._rotateForbid.input.oninput = (ev: Event) => {
				this._rotateForbid.value = !(ev.target as HTMLInputElement).checked;
				this._setDirty(true);
				this._requestSaveProps();
			};

			this._rotateForbid.input.onchange = () => {
				this._pushStateToHistory();
			};

			this._rotateForbid.area = <div id="rotateForbidArea">
				<span class="areaTitle">Rotation autorisée</span>
				<div class="checkValue">
					{this._rotateForbid.input}
				</div>
			</div> as HTMLDivElement;
		}
		if (this._stdCharSize || this._limitCharSize || this._rotateForbid) {
			let sizeAreas = null;
			if (this._stdCharSize || this._limitCharSize) {
				sizeAreas = <div id="sizeAreas">
					{this._stdCharSize && this._stdCharSize.area}
					{this._limitCharSize && this._limitCharSize.area}
				</div>;
			}

			const saveAction = new Action<ImgSize>("save")
				.setIcon("/@skin@/edit/wed/save.svg")
				.setLabel("Enregistrer")
				.setEnabled((ctx) => {
					return ctx._isDirty;
				})
				.setExecute(async (ctx) => {
					await ctx._saveProps();
				});

			const undoAction = new Action<ImgSize>("undo")
				.setIcon("/@skin@/edit/wed/undo.svg")
				.setLabel("Annuler")
				.setExecute(async (ctx) => {
					if (ctx._currentStateIdx > 0) {
						ctx._currentStateIdx--;
						ctx._restoreStateFromHistory();
					} else {
						POPUP.showNotifInfo("Aucune action à annuler", ctx, null, ctx._notifAnchor)
					}
				});

			const redoAction = new Action<ImgSize>("redo")
				.setIcon("/@skin@/edit/wed/redo.svg")
				.setLabel("Refaire")
				.setExecute(async (ctx) => {
					if (ctx._currentStateIdx < ctx._history.length - 1) {
						ctx._currentStateIdx++;
						ctx._restoreStateFromHistory();
					} else {
						POPUP.showNotifInfo("Aucune action à refaire", ctx, null, ctx._notifAnchor)
					}

				});
			this._toolbar = new BarActions<ImgSize>().initialize({
				actions: [saveAction, undoAction, redoAction],
				actionContext: this
			});
			sr.appendChild(<aside>
				{this._toolbar}
				{sizeAreas}
				{this._rotateForbid && this._rotateForbid.area}
			</aside>);

			// Enregistrement des changements d'états sur le flux des props
			const {env} = this.reg;
			this._propsWspRef = WSP.buildWspRef(env.wsp.code, SRC.addLeafToUri(env.longDesc.srcUri, 'props.xml'));
			this._setDirty(false);

			this._onNewWspWorkingState = (wspRef: wspRef, wspCd: wspCd, srcRef: srcRef, st: EWspWorkingSt) => {
				if (wspRef === this._propsWspRef) {
					this._locked = st === EWspWorkingSt.w;
					this._refresh();
				}
			};
			env.place.eventsMgr.on("newWspWorkingState", this._onNewWspWorkingState);

			// Listener sur les changements de la ressource
			this._onWspUriChange = async (msg: JWspUriChangeMsg) => {
				if (msg.type == EWspChangesEvts.u && SRC.srcRef(env.longDesc) === SRC.srcRef(msg)) {
					// On évite le refresh si une édition est en cours.
					// Dans certain cas, il est possible d'avoir une édition avant les évènements d'update initiaux (après import)
					// A voir si cela pose problème en pratique sur les éditions concurrentes
					if (this._savePropsReq) return;
					await Promise.all([this._loadDims(), this._loadProps()]);
					await this._refresh();
				}
			};
			env.place.eventsMgr.on("wspUriChange", this._onWspUriChange);
		}

		if (init && (this._stdCharSize || this._limitCharSize)) {
			this._rulerElem = <div id="sizeRuler"/> as HTMLDivElement;

			if (init.stdCharSize) this._txtRefElem.textContent = init.stdCharSize.textRef || STDCHARSIZE_TXTREF;
			else if (init.limitCharSize) this._txtRefElem.textContent = init.limitCharSize.textRef || LIMITCHARSIZE_TXTREF;

			POPUP.promiseTooltip(this._txtRefElem, () => <div>{TXTREF_HELP}</div>,
				{
					skinOver: "wsp-imgsize/txtref-tooltip",
					anchor: {
						posFrom: this._txtRefElem,
						initWidth: '640px',
						fromY: 'bottom',
						marginY: 3
					}
				});

			this._sizeRefElem = <div id="sizeRef">
				{this._rulerElem}
				{this._txtRefElem}
			</div> as HTMLDivElement;
		}
		sr.appendChild(<div id="mainArea">
			{init.actions ? <BarActions î={{actions: init.actions, actionContext: init.actionContext || this.reg.env.srcView, reg: this.reg} as OBarActionsInit<IShortDescCtx>}/> : undefined}
			<div id="scrollBox">
				{this._sizeBox}
				{this._sizeRefElem}
			</div>
		</div>);

		await Promise.all([this._loadDims(), this._loadProps()]);
	}

	disconnectedCallback() {
		const {place} = this.reg.env;
		if (this._onNewWspWorkingState) place.eventsMgr.on("newWspWorkingState", this._onNewWspWorkingState);
		if (this._onWspUriChange) place.eventsMgr.removeListener("wspUriChange", this._onWspUriChange);
	}

	protected async _refresh() {
		if (this._dims) this._updateProps();
		const readOnly = this._locked || !this.reg.hasPerm(this._writePerms) || !this._dimsTransform || !this._dims;

		if (this._stdCharSize) {
			this._onStdCharSizeUpdate();
			const {rangeInput, percentInput, valueInput} = this._stdCharSize;
			rangeInput.disabled = percentInput.disabled = valueInput.disabled = readOnly;
		}
		if (this._limitCharSize) {
			this._onLimitCharSizeUpdate();
			const {rangeInput, percentInput, valueInput} = this._limitCharSize;
			rangeInput.disabled = percentInput.disabled = valueInput.disabled = readOnly;
		}
		if (this._rotateForbid) {
			this._rotateForbid.input.checked = !this._rotateForbid.value;
			this._rotateForbid.input.disabled = readOnly;
		}
		if (this._toolbar) DOM.setHidden(this._toolbar, readOnly);
	}

	async _loadDims() {
		const {env} = this.reg;
		try {
			if (!this._dimsTransform) return;
			const dimsText = await WSP.fetchStreamText(env.wsp, this, env.itemType.getMainStreamSrcUri(env.longDesc), this._dimsTransform);
			const dims = dimsText.split(';', 3);
			this._dims = [parseFloat(dims[0]), parseFloat(dims[1])];
			if (this._dims[0] == -1 || this._dims[1] == -1) throw "Invalid dimensions";
			this._dpi = dims[2] ? parseFloat(dims[2]) : undefined;
		} catch (e) {
			this._dims = undefined;
			this._dpi = undefined;
			console.warn("Unable to retrieve the resource dimensions");
		}
	}

	async _loadProps() {
		const {env} = this.reg;
		try {
			const propsDoc = await ITEM.fetchDom(env.wsp, this, `${env.longDesc.srcUri}/props.xml`, true);
			this._propsElt = propsDoc ? propsDoc.documentElement : null;
		} catch {}
	}

	protected _updateProps() {
		if (this._stdCharSize) {
			const stdCharSizeAttr = this._propsElt?.getAttribute('standardCharSize');
			let invValue = NaN;
			if (stdCharSizeAttr) invValue = parseFloat(stdCharSizeAttr);
			if (isNaN(invValue)) {
				const {defaultValue} = this._stdCharSize;
				if (typeof defaultValue === 'number') invValue = defaultValue;
				else invValue = defaultValue(this._dims[0], this._dpi);
			}
			this._stdCharSize.value = 1000 / invValue;
		}

		if (this._limitCharSize) {
			this._limitCharSize.value = this._limitCharSize.defaultValue; //reset
			const limitCharSizeAttr = this._propsElt?.getAttribute('limitCharSize');
			if (limitCharSizeAttr) {
				const limitCharSize = parseFloat(limitCharSizeAttr);
				if (!isNaN(limitCharSize)) {
					const stdCharSize = this._stdCharSize ? this._stdCharSize.value : 100;
					this._limitCharSize.value = 1000 / stdCharSize * 100 / limitCharSize;
				}
			}
		}

		if (this._rotateForbid) {
			if (this._propsElt?.hasAttribute('rotateForbidden')) {
				this._rotateForbid.value = this._propsElt.getAttribute('rotateForbidden') === "true";
			} else {
				this._rotateForbid.value = this._rotateForbid.defaultValue;
			}
		}

		this._pushStateToHistory();
	}

	protected _pushStateToHistory() {
		const currentState: IImgSizeState = {
			stdCharSize: this._stdCharSize ? this._stdCharSize.value : undefined,
			limitCharSize: this._limitCharSize ? this._limitCharSize.value : undefined,
			rotateForbid: this._rotateForbid ? this._rotateForbid.value : undefined
		};
		const lastState = this._history[this._currentStateIdx];
		if (!lastState || JSON.stringify(lastState) != JSON.stringify(currentState)) {
			this._history.push(currentState);
			this._currentStateIdx = this._history.length - 1;
		}
	}

	protected _restoreStateFromHistory() {
		const currentState = this._history[this._currentStateIdx];
		if (this._stdCharSize) {
			this._stdCharSize.value = currentState.stdCharSize;
			this._onStdCharSizeUpdate();
		}
		if (this._limitCharSize) {
			this._limitCharSize.value = currentState.limitCharSize;
			this._onLimitCharSizeUpdate();
		}
		if (this._rotateForbid) {
			this._rotateForbid.value = currentState.rotateForbid;
			this._rotateForbid.input.checked = !this._rotateForbid.value;
		}
		this._setDirty(true, true);
		this._requestSaveProps();
	}

	protected _requestSaveProps() {
		if (this._savePropsReq) clearTimeout(this._savePropsReq);
		this._savePropsReq = setTimeout(() => {
			this._savePropsReq = 0;
			this._saveProps();
		}, 2000);
	}

	protected async _saveProps() {
		const {env} = this.reg;
		const propsDoc = DOM.newDomDoc();
		const propsElt = propsDoc.appendChild(document.createElement('imgProps'));
		const stdCharSize = this._stdCharSize ? this._stdCharSize.value : 100;
		if (this._stdCharSize) propsElt.setAttribute('standardCharSize', (1000 / stdCharSize).toString());
		if (this._limitCharSize) propsElt.setAttribute('limitCharSize', (1000 / stdCharSize * 100 / this._limitCharSize.value).toString());
		if (this._rotateForbid) propsElt.setAttribute('rotateForbidden', this._rotateForbid.value.toString());
		await ITEM.update(env.wsp, this, `${env.longDesc.srcUri}/props.xml`, DOM.ser(propsDoc));
		this._setDirty(false);
	}

	protected _onStdCharSizeUpdate(from?: HTMLElement) {
		if (!this._dims || this._updateStdCharReq) return;
		this._updateStdCharReq = requestAnimationFrame(() => {
			this._updateStdCharReq = 0;
			const {value, rangeInput, percentInput, valueInput} = this._stdCharSize;
			const stdCharDims = this._dims.map((dim) => dim * value / 100);
			if (!this._limitCharSize || !this._limitCharSize.area.matches(':focus-within')) this._updateImgDims(stdCharDims);

			const fixedValue = value.toFixed(1).toString();
			if (rangeInput != from) rangeInput.value = fixedValue;
			if (percentInput != from) percentInput.value = fixedValue;
			if (valueInput != from) valueInput.value = (stdCharDims[0] / 6).toFixed(1).toString();
		})

	}

	_onLimitCharSizeUpdate(from?: HTMLElement) {
		if (!this._dims || this._updateLimitCharReq) return;
		this._updateLimitCharReq = requestAnimationFrame(() => {
			this._updateLimitCharReq = 0;
			const {value, rangeInput, percentInput, valueInput} = this._limitCharSize;
			const stdCharDims = this._stdCharSize ? this._dims.map((dim) => dim * this._stdCharSize.value / 100) : this._dims;
			const limitCharDims = stdCharDims.map((dim) => dim * value / 100);
			if (this._limitCharSize.area.matches(':focus-within')) this._updateImgDims(limitCharDims);

			const fixedValue = value.toFixed(1).toString();
			if (rangeInput != from) rangeInput.value = fixedValue;
			if (percentInput != from) percentInput.value = fixedValue;
			if (valueInput != from) valueInput.value = (limitCharDims[0] / 6).toFixed(1).toString();
		});
	}

	_setDirty(dirty: boolean, fromHistory = false) {
		this._isDirty = dirty;
		// Changement d'état des props pour les locker/délocker les autres ImgSize ouvert sur cet item
		this.reg.env.place.setState(this._propsWspRef, dirty ? EWspWorkingSt.w : EWspWorkingSt.r);
		this._toolbar.refreshContent();
		if (dirty && !fromHistory) {
			this._history.length = this._currentStateIdx + 1;
		}
	}

	_updateImgDims(dims: number[]) {
		this._sizeBox.style.width = dims[0] + 'px';
		this._sizeBox.style.height = dims[1] + 'px';
		this._rulerElem.style.width = dims[0] + 'px';
	}
}

customElements.define("wsp-imgsize", ImgSize);

REG.reg.registerSkin("wsp-imgsize", 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
	}

	[hidden] {
		display: none;
	}

	aside {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		margin-inline-end: 0.5em;
		overflow-y: auto;
	}

	#sizeAreas {
		display: flex;
		min-height: auto;
		min-width: 0;
		flex: 1;
	}

	.sizeArea {
		display: flex;
		min-height: auto;
		min-width: 0;
		flex-direction: column;
		flex: 1;
		align-items: center;
		margin: 0.2em 0.3em;
		width: 6.1em;
	}

	#rotateForbidArea {
		display: flex;
		min-height: auto;
		min-width: 0;
		flex-direction: column;
		align-items: center;
		margin: 0.2em;
	}

	.areaTitle {
		color: var(--alt1-color);
		text-align: center;
		font-weight: bold;
		margin: 0.2em 0;
	}

	label {
		display: block;
	}

	label > span {
		margin-inline-start: 0.1em;
	}

	input[type=range] {
		margin: 0;
		-webkit-appearance: slider-vertical;
		flex: 1;
		width: 3em;
		min-height: 8em;
	}

	input[type=number] {
		width: 4em;
		text-align: center;
		margin: 0.2em 0;
	}

	.numberValue {
		color: var(--alt2-color);
	}

	.checkValue {
		padding: 2px;
		margin: 2px;
	}

	input[type=checkbox] {
		display: block;
		margin: 0;
	}

	#mainArea {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex: 1;
		flex-direction: column;
	}

	#scrollBox {
		display: flex;
		flex-direction: column;
		min-height: 0;
		min-width: 0;
		overflow: auto;
		flex: 1;
	}

	#sizeBox {
		display: block;
		margin: auto;
		flex: 0 0 auto;
	}

	::slotted(*) {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	#sizeRuler {
		margin: 0.5em auto;
		background-image: url(/@skin@/wsp/widgets/imgSize/ruler.png);
		background-repeat: repeat-x;
		height: 10px;
	}

	#sizeRef {
		position: sticky;
		bottom: 0;
	}


	#txtRef {
		width: 640px;
		box-sizing: border-box;
		margin: 0.5em auto;
		border-color: var(--border-color);
		border-inline-start: dotted 1px;
		border-inline-end: dotted 1px;
		padding: 3px;
		font-size: 10pt;
		text-align: justify;
		background-color: #FFFFFFBB;
		color: #000c18;
		cursor: help;
	}
`);

REG.reg.registerSkin("wsp-imgsize/txtref-tooltip", 1, /* language=CSS */ `
	::slotted(div) {
		padding: 0.5em;
	}
`);


